/**
 * onboarding.ts — logic thuần của trang "Bắt đầu" (/onboarding), tách riêng để kiểm thử.
 *
 * Backend: GET /onboarding/status, POST /onboarding/dismiss (ecdp_backend_source/src/modules/onboarding).
 */
import type { AuthUser, Role } from '@/types/api'
import { i18n } from '@/lib/i18n'

export interface OnboardingStatus {
  channelsConnected: number
  firstReplyDone: boolean
  aiConfigured: boolean
  automationCount: number
  membersCount: number
  dismissedAt: string | null
  completedAt: string | null
  /** Chủ sở hữu tạo sau mốc triển khai (người dùng mới) — backend quyết định. */
  eligible: boolean
  /** Bước (1..5) mở gần nhất từ trang Bắt đầu — backend cũ có thể chưa trả. */
  lastStep?: number | null
  /** Thời điểm lần đầu mỗi bước xong (connect, reply, ai, automation, invite). */
  stepsDone?: Partial<Record<OnboardingStepKey, string>>
}

export const ONBOARDING_PATH = '/onboarding'

export type StepState = 'done' | 'current' | 'locked'

export interface OnboardingSteps {
  connect: StepState
  reply: StepState
  doneCount: number
  total: 2
  allDone: boolean
}

/** Trạng thái hai bước: bước 2 khoá tới khi có kênh. */
export function computeSteps(s: Pick<OnboardingStatus, 'channelsConnected' | 'firstReplyDone'> | undefined): OnboardingSteps {
  const connected = (s?.channelsConnected ?? 0) > 0
  const replied = !!s?.firstReplyDone
  const connect: StepState = connected ? 'done' : 'current'
  // Đã trả lời được (vd qua kênh vừa gỡ) nhưng chưa có kênh → bước 2 vẫn tính xong.
  const reply: StepState = replied ? 'done' : connected ? 'current' : 'locked'
  const doneCount = Number(connected) + Number(replied)
  return { connect, reply, doneCount, total: 2, allDone: doneCount === 2 }
}

/** Phần trăm thanh tiến độ. */
export const progressPercent = (st: OnboardingSteps) => Math.round((st.doneCount / st.total) * 100)

const SESSION_KEY = 'ecdp_onboarding_shown'

/** Đã tự chuyển tới /onboarding trong phiên trình duyệt này (tránh ép lại mỗi lần vào `/`). */
export function wasShownThisSession(userId: string, store: Pick<Storage, 'getItem'> | null = safeSession()): boolean {
  try {
    return store?.getItem(SESSION_KEY) === userId
  } catch {
    return false
  }
}

export function markShownThisSession(userId: string, store: Pick<Storage, 'setItem'> | null = safeSession()) {
  try {
    store?.setItem(SESSION_KEY, userId)
  } catch { /* chế độ riêng tư */ }
}

function safeSession(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

/** Cần gọi API trạng thái ở `/` không: chỉ chủ sở hữu, không đang xem hộ ai. Người khác đi thẳng trang đầu. */
export function mayNeedOnboarding(user: Pick<AuthUser, 'role' | 'impersonatedBy' | 'platformActorId'> | null | undefined): boolean {
  return !!user && user.role === 'owner' && !user.impersonatedBy && !user.platformActorId
}

/**
 * Luật chuyển hướng ở `/`: người dùng MỚI (backend `eligible` — chủ sở hữu tạo sau mốc) chưa bỏ qua,
 * chưa hoàn thành, chưa được chuyển trong phiên này → /onboarding. Người dùng cũ không bao giờ bị ép.
 */
export function shouldForceOnboarding(
  status: OnboardingStatus | undefined,
  user: Pick<AuthUser, 'id' | 'role' | 'impersonatedBy' | 'platformActorId'> | null | undefined,
  shownThisSession = false,
): boolean {
  if (!status || !user || !mayNeedOnboarding(user)) return false
  if (!status.eligible || status.dismissedAt || status.completedAt) return false
  return !shownThisSession
}

/** Kênh nổi bật ở bước 1 (lưới 2×2), theo thứ tự hiển thị. */
export const FEATURED_CHANNELS = ['zalo-personal', 'zalo-oa', 'facebook', 'website'] as const

/** Tên hiển thị riêng trên trang Bắt đầu (khác tên ở danh mục kênh). */
export const FEATURED_LABELS: Partial<Record<string, string>> = {
  get website() { return i18n.t('sharedMisc:onboarding.featuredWebsite') },
}

const MANAGERS: Role[] = ['owner', 'admin', 'manager']

export interface QuickActionAccess {
  ai: boolean
  automation: boolean
  invite: boolean
  connect: boolean
}

/** Nút nào người dùng dùng được (khớp guard route + khoá quyền backend). `perms` chưa tải → ẩn. */
export function quickActionAccess(role: Role | undefined, perms: Set<string> | undefined): QuickActionAccess {
  const has = (k: string) => !!perms?.has(k)
  const manager = !!role && MANAGERS.includes(role)
  return {
    ai: has('ai.view') && has('ai.update'),
    automation: manager && has('automation.view') && has('automation.create'),
    invite: manager && has('employees.view') && has('employees.create'),
    connect: has('integrations.view') && has('integrations.create'),
  }
}

/**
 * Liên kết hỗ trợ — cấu hình bằng biến môi trường lúc build.
 *   VITE_INTEGRATION_GUIDE_URL: tài liệu hướng dẫn tích hợp. TẠM ẨN (30/09/2026): chưa có tài liệu — mục chỉ hiện
 *                               khi đặt biến này (hoặc bật lại `SHOW_INTEGRATION_GUIDE`).
 *   VITE_PRODUCT_ADVISOR_URL:   kênh liên hệ cố vấn sản phẩm. Mặc định tạm: Zalo OA eCDP.
 */
/** Cờ tạm ẩn "Xem Hướng dẫn Tích hợp" — bật khi có tài liệu hướng dẫn thật. */
const INTEGRATION_GUIDE_URL = (import.meta.env.VITE_INTEGRATION_GUIDE_URL as string | undefined)?.trim() || ''
export const SHOW_INTEGRATION_GUIDE = INTEGRATION_GUIDE_URL !== ''

export const ONBOARDING_LINKS = {
  integrationGuide: INTEGRATION_GUIDE_URL,
  productAdvisor: (import.meta.env.VITE_PRODUCT_ADVISOR_URL as string | undefined)?.trim() || 'https://zalo.me/s/4332572700089944369/',
}

export const isExternalUrl = (u: string) => /^(https?:|mailto:|tel:)/i.test(u)

// ─────────────────────────────────────────────────────────────────────────────
// Luồng quay lại trang Bắt đầu theo từng bước
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Số bước trên trang Bắt đầu (khớp `#step-<n>` và backend `ONBOARDING_STEP_KEYS`):
 *   1 kết nối kênh · 2 trả lời tin đầu · 3 nhân viên AI · 4 quy trình tự động · 5 mời đồng nghiệp.
 */
export const ONBOARDING_STEPS = [
  { no: 1, key: 'connect', get title() { return i18n.t('sharedMisc:onboarding.steps.connect') } },
  { no: 2, key: 'reply', get title() { return i18n.t('sharedMisc:onboarding.steps.reply') } },
  { no: 3, key: 'ai', get title() { return i18n.t('sharedMisc:onboarding.steps.ai') } },
  { no: 4, key: 'automation', get title() { return i18n.t('sharedMisc:onboarding.steps.automation') } },
  { no: 5, key: 'invite', get title() { return i18n.t('sharedMisc:onboarding.steps.invite') } },
] as const

export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number]['key']
export type OnboardingStepNo = (typeof ONBOARDING_STEPS)[number]['no']

/** Giá trị DUY NHẤT được chấp nhận cho `?from=` — không nhận URL tự do (chống open-redirect). */
export const ONBOARDING_FROM = 'onboarding'

/** Đích quay về sau OAuth — backend chỉ nhận đường trong danh sách cho phép. */
export const OAUTH_RETURN_TO = ONBOARDING_PATH

export const stepTitle = (n: OnboardingStepNo) => ONBOARDING_STEPS[n - 1].title

/** Số bước hợp lệ 1..5 (số nguyên, nhận chuỗi số) hoặc null. */
export function parseStepNo(v: unknown): OnboardingStepNo | null {
  const n = typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= ONBOARDING_STEPS.length ? (n as OnboardingStepNo) : null
}

/**
 * Trang hiện tại được mở từ trang Bắt đầu không: CHỈ khi `from` đúng bằng `onboarding`.
 * Trả `{ step }` (bước có thể null nếu thiếu / sai) hoặc null.
 */
export function readOnboardingOrigin(sp: Pick<URLSearchParams, 'get'>): { step: OnboardingStepNo | null } | null {
  if (sp.get('from') !== ONBOARDING_FROM) return null
  return { step: parseStepNo(sp.get('step')) }
}

/** Gắn `from=onboarding&step=<n>` vào đường nội bộ (giữ query + hash sẵn có). */
export function withOnboardingOrigin(to: string, step: OnboardingStepNo): string {
  const hashAt = to.indexOf('#')
  const hash = hashAt >= 0 ? to.slice(hashAt) : ''
  const beforeHash = hashAt >= 0 ? to.slice(0, hashAt) : to
  const qAt = beforeHash.indexOf('?')
  const path = qAt >= 0 ? beforeHash.slice(0, qAt) : beforeHash
  const q = new URLSearchParams(qAt >= 0 ? beforeHash.slice(qAt + 1) : '')
  q.set('from', ONBOARDING_FROM)
  q.set('step', String(step))
  return `${path}?${q.toString()}${hash}`
}

/** Bỏ `from` / `step` khỏi query (khi người dùng chọn "Ở lại trang này"). */
export function stripOnboardingOrigin(search: string): string {
  const q = new URLSearchParams(search)
  q.delete('from')
  q.delete('step')
  const s = q.toString()
  return s ? `?${s}` : ''
}

type StatusLike = Pick<OnboardingStatus, 'channelsConnected' | 'firstReplyDone' | 'aiConfigured' | 'automationCount' | 'membersCount'>

/** Trạng thái xong của từng bước theo dữ liệu thật (không dùng mốc lịch sử `stepsDone`). */
export function stepDoneMap(s: Partial<StatusLike> | undefined): Record<OnboardingStepNo, boolean> {
  return {
    1: (s?.channelsConnected ?? 0) > 0,
    2: !!s?.firstReplyDone,
    3: !!s?.aiConfigured,
    4: (s?.automationCount ?? 0) > 0,
    5: (s?.membersCount ?? 0) > 1,
  }
}

/**
 * Bước kế tiếp nên làm sau khi vừa xong `justDone` (coi như đã xong dù trạng thái chưa kịp cập nhật):
 * bước chưa xong đầu tiên SAU nó, không có thì bước chưa xong đầu tiên từ đầu; hết → null.
 * `available` lọc bước người dùng không có quyền làm (vd không được mời nhân viên).
 */
export function nextPendingStep(
  status: Partial<StatusLike> | undefined,
  justDone?: OnboardingStepNo | null,
  available: (n: OnboardingStepNo) => boolean = () => true,
): OnboardingStepNo | null {
  const done = stepDoneMap(status)
  if (justDone) done[justDone] = true
  // Bước 2 chỉ làm được khi đã có kênh.
  const pending = ONBOARDING_STEPS.map((x) => x.no).filter(
    (n) => !done[n] && available(n) && (n !== 2 || done[1]),
  )
  if (!pending.length) return null
  return pending.find((n) => !justDone || n > justDone) ?? pending[0]
}

/** Đích quay về trang Bắt đầu: cuộn tới bước `n` (hoặc đầu trang). */
export const onboardingReturnPath = (n: OnboardingStepNo | null | undefined) =>
  n ? `${ONBOARDING_PATH}#step-${n}` : ONBOARDING_PATH

/** Bước cần cuộn tới khi mở trang Bắt đầu: `#step-n` trên URL, rồi bước mở gần nhất. */
export function stepFromHash(hash: string): OnboardingStepNo | null {
  const m = /^#step-(\d+)$/.exec(hash)
  return m ? parseStepNo(m[1]) : null
}

// ── Bước mở gần nhất (sessionStorage) ────────────────────────────────────────

export const LAST_STEP_KEY = 'onboarding:lastStep'

export function rememberLastStep(n: OnboardingStepNo, store: Pick<Storage, 'setItem'> | null = safeSession()) {
  try {
    store?.setItem(LAST_STEP_KEY, String(n))
  } catch { /* chế độ riêng tư */ }
}

export function readLastStep(store: Pick<Storage, 'getItem'> | null = safeSession()): OnboardingStepNo | null {
  try {
    return parseStepNo(store?.getItem(LAST_STEP_KEY) ?? null)
  } catch {
    return null
  }
}

// ── Thanh "Tiếp tục thiết lập" + mục "Bắt đầu" ở menu trái ───────────────────

/** Khoá cũ (bản trước nhớ "×" trong sessionStorage → tải lại trang vẫn ẩn). Chỉ còn dùng để dọn. */
export const RESUME_CLOSED_KEY = 'onboarding:resumeClosed'

/**
 * "×" trên thanh nổi chỉ ẩn trong **lượt đăng nhập đang mở của tab này**: nhớ trong bộ nhớ (không lưu trữ),
 * gắn với id người dùng. Tải lại trang, đăng xuất rồi vào lại, hoặc người khác đăng nhập → hiện lại.
 * Chỉ khi hoàn tất thiết lập (completedAt) thì thanh mới thôi hiện hẳn.
 */
let resumeClosedFor: string | null = null

export function isResumeClosed(userId: string | null | undefined): boolean {
  return !!userId && resumeClosedFor === userId
}

export function closeResumeForSession(userId: string | null | undefined) {
  resumeClosedFor = userId ?? null
}

/** Gọi khi đăng xuất. */
export function resetResumeClosed() {
  resumeClosedFor = null
}

/** Dọn khoá sessionStorage của bản cũ để không còn ẩn sau khi tải lại. */
export function clearLegacyResumeClosed(store: Pick<Storage, 'removeItem'> | null = safeSession()) {
  try {
    store?.removeItem(RESUME_CLOSED_KEY)
  } catch { /* chế độ riêng tư */ }
}

/**
 * Có hiện lối vào "Bắt đầu" không (mục menu trái + thanh nổi): chủ sở hữu mới (eligible), không xem hộ,
 * chưa hoàn thành 2 bước, chưa bấm "Bỏ qua".
 */
export function showOnboardingEntry(
  status: Pick<OnboardingStatus, 'eligible' | 'dismissedAt' | 'completedAt'> | undefined,
  user: Pick<AuthUser, 'role' | 'impersonatedBy' | 'platformActorId'> | null | undefined,
): boolean {
  if (!status || !mayNeedOnboarding(user)) return false
  return status.eligible && !status.dismissedAt && !status.completedAt
}

/** Thanh nổi: như lối vào menu, thêm — không hiện ngay trên /onboarding, và không hiện khi đã đóng trong phiên. */
export function resumeBarVisible(opts: {
  pathname: string
  status: Pick<OnboardingStatus, 'eligible' | 'dismissedAt' | 'completedAt'> | undefined
  user: Pick<AuthUser, 'role' | 'impersonatedBy' | 'platformActorId'> | null | undefined
  closedThisSession: boolean
}): boolean {
  if (opts.closedThisSession) return false
  if (opts.pathname === ONBOARDING_PATH || opts.pathname.startsWith(`${ONBOARDING_PATH}/`)) return false
  return showOnboardingEntry(opts.status, opts.user)
}
